import { Box, IconButton, Stack, useTheme } from "@mui/material";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { GoalActionBlockedDrawer } from "web_myplann/Goals/Components/GoalActionBlockedDrawer";
import { GoalCreatedPinDrawer } from "web_myplann/Goals/Components/GoalCreatedPinDrawer";
import { GoalDetailComposition } from "web_myplann/Goals/Components/GoalDetailComposition";
import { GoalDetailHero } from "web_myplann/Goals/Components/GoalDetailHero";
import { GoalDetailLoanCalculator } from "web_myplann/Goals/Components/GoalDetailLoanCalculator";
import { GoalDetailRentPlan } from "web_myplann/Goals/Components/GoalDetailRentPlan";
import { GoalDetailSkeleton } from "web_myplann/Goals/Components/GoalDetailSkeleton";
import { GoalDetailWhatIf } from "web_myplann/Goals/Components/GoalDetailWhatIf";
import { GoalPinningSection } from "web_myplann/Goals/Components/GoalPinningSection";
import { SetOwnFundsDrawer } from "web_myplann/Goals/Components/SetOwnFundsDrawer";
import { useGoalActionLock } from "web_myplann/Goals/Hooks/useGoalActionLock";
import { useGoalDetail } from "web_myplann/Goals/Hooks/useGoalDetail";
import { useSetOwnFunds } from "web_myplann/Goals/Hooks/useSetOwnFunds";
import { useWelcomePin } from "web_myplann/Goals/Hooks/useWelcomePin";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { getTypedParam } from "web_myplann/Shared/Utils/getTypedParam";

export const GoalDetailPage = () => {
	const theme = useTheme();
	const { t } = useResource();
	const routeParams = useParams<{ id: string }>();
	const goalId = getTypedParam(routeParams, "id", Number) ?? null;

	const { goal, dto: goalDto, isLoading } = useGoalDetail(goalId);
	const {
		blockedOpen,
		closeBlocked,
		requestEdit,
		onLockedAction,
		messageAdvisor,
	} = useGoalActionLock(goal);
	const {
		welcomePinOpen,
		welcomePinOwnFunds,
		welcomePinTrigger,
		closeWelcomePin,
		assignWelcomePin,
		clearWelcomePinTrigger,
		triggerWelcomePin,
	} = useWelcomePin();
	const [ownFundsOpen, setOwnFundsOpen] = useState(false);

	const { saveOwnFunds, isSaving } = useSetOwnFunds({
		goalId,
		goalDto,
		onSaved: () => {
			setOwnFundsOpen(false);
			triggerWelcomePin(true);
		},
	});

	return (
		<MyPlannLayout
			bgColor={theme.palette.surface.muted}
			disableContentPadding
			topBar={
				<NavigationHeader
					title={t(Resources.Goals.Detail.title)}
					bgColor={theme.palette.background.paper}
					defaultRoute={Routing.Goals.GoalsList.path()}
					endComponent={
						goal &&
						goalId !== null && (
							<IconButton variant="soft" onClick={requestEdit}>
								<Pencil size={theme.iconSize.sm} />
							</IconButton>
						)
					}
				/>
			}
		>
			{isLoading && !goal && <GoalDetailSkeleton />}

			{goal && (
				<Stack>
					<GoalDetailHero
						goal={goal}
						onAddOwnFunds={() => setOwnFundsOpen(true)}
					/>
					<GoalPinningSection
						goal={goal}
						goalDto={goalDto}
						welcomePinTrigger={welcomePinTrigger}
						onWelcomePinTriggerHandled={clearWelcomePinTrigger}
						onLockedAction={onLockedAction}
					/>
					<Box mt={1}>
						<GoalDetailLoanCalculator goal={goal} />
					</Box>
					<Box mt={1}>
						<GoalDetailRentPlan goal={goal} />
					</Box>
					<Box mt={1}>
						<GoalDetailWhatIf goal={goal} />
					</Box>
					<Box mt={1}>
						<GoalDetailComposition goal={goal} />
					</Box>
				</Stack>
			)}
			<GoalActionBlockedDrawer
				open={blockedOpen}
				onClose={closeBlocked}
				onMessageAdvisor={messageAdvisor}
			/>
			<SetOwnFundsDrawer
				open={ownFundsOpen}
				isSaving={isSaving}
				onClose={() => setOwnFundsOpen(false)}
				onSubmit={saveOwnFunds}
			/>
			<GoalCreatedPinDrawer
				open={welcomePinOpen}
				ownFunds={welcomePinOwnFunds}
				onClose={closeWelcomePin}
				onAssign={assignWelcomePin}
			/>
		</MyPlannLayout>
	);
};
